import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  CreditCard,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { AdminAnalyticsCharts } from '@/components/admin/AdminAnalyticsCharts';
import { AdminMetricCard } from '@/components/admin/AdminMetricCard';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ROUTES } from '@/constants/routes';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminTransactionService, getApiErrorMessage } from '@/services';
import type { AdminTransactionStats } from '@/types/transactions';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { formatTransactionNaira } from '@/utils/transactionFormatters';
import { notify } from '@/utils/notify';

export function AdminDashboardHome() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'7d' | '30d' | 'all'>('30d');
  const [stats, setStats] = useState<AdminTransactionStats | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminTransactionService.getStats(period);
      setStats(res.data ?? null);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load dashboard stats'));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const userStats = stats?.users;
  const txStats = stats?.transactions;

  const periodLabel = period === '7d' ? 'Last 7 days' : period === '30d' ? 'Last 30 days' : 'All time';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-bold sm:text-2xl">Admin Dashboard</h1>
          <p className={cn('text-sm', shell.cardMuted)}>Platform overview, analytics, and profit insights</p>
        </div>
        <select
          className={cn('rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2', shell.select)}
          value={period}
          onChange={(e) => setPeriod(e.target.value as typeof period)}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      {loading ? (
        <Loader label="Loading dashboard..." />
      ) : (
        <>
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">Platform users</h2>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <AdminMetricCard
                theme={theme}
                title="Total users"
                value={(userStats?.total ?? 0).toLocaleString('en-NG')}
                subtitleLines={[
                  `${(userStats?.active ?? 0).toLocaleString('en-NG')} active · ${(userStats?.blocked ?? 0).toLocaleString('en-NG')} blocked`,
                  `${(userStats?.agents ?? 0).toLocaleString('en-NG')} agents · ${(userStats?.subscribers ?? 0).toLocaleString('en-NG')} subscribers`,
                ]}
                icon={Users}
                iconBg="bg-primary"
              />
              <AdminMetricCard
                theme={theme}
                title="Wallet balance"
                value={formatTransactionNaira(userStats?.totalWalletBalance ?? 0)}
                subtitleLines={['Combined balance across all users']}
                icon={Wallet}
                iconBg="bg-blue-500"
                valueSize="md"
              />
              <AdminMetricCard
                theme={theme}
                title="Active agents"
                value={(userStats?.agents ?? 0).toLocaleString('en-NG')}
                subtitleLines={[`${(userStats?.subscribers ?? 0).toLocaleString('en-NG')} subscribers on platform`]}
                icon={Briefcase}
                iconBg="bg-violet-500"
              />
              <AdminMetricCard
                theme={theme}
                title="Active users"
                value={(userStats?.active ?? 0).toLocaleString('en-NG')}
                subtitleLines={[`${(userStats?.pending ?? 0).toLocaleString('en-NG')} pending verification`]}
                icon={UserCheck}
                iconBg="bg-emerald-500"
              />
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
              Transactions · {periodLabel}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <AdminMetricCard
                theme={theme}
                title="Total transactions"
                value={(txStats?.totalCount ?? 0).toLocaleString('en-NG')}
                subtitleLines={[
                  `${(txStats?.successCount ?? 0).toLocaleString('en-NG')} successful`,
                  `${(txStats?.pendingCount ?? 0).toLocaleString('en-NG')} pending · ${(txStats?.failedCount ?? 0).toLocaleString('en-NG')} failed`,
                ]}
                icon={CreditCard}
                iconBg="bg-amber-500"
              />
              <AdminMetricCard
                theme={theme}
                title="Debit volume"
                value={formatTransactionNaira(txStats?.debitVolume ?? 0)}
                subtitleLines={[`Credit volume ${formatTransactionNaira(txStats?.creditVolume ?? 0)}`]}
                icon={TrendingUp}
                iconBg="bg-orange-500"
                valueSize="md"
              />
              <AdminMetricCard
                theme={theme}
                title="Total profit"
                value={formatTransactionNaira(txStats?.totalProfit ?? 0)}
                subtitleLines={['From tracked cost vs sell price']}
                icon={Wallet}
                iconBg="bg-emerald-500"
                valueSize="md"
              />
              <AdminMetricCard
                theme={theme}
                title="Success rate"
                value={
                  txStats && txStats.totalCount > 0
                    ? `${Math.round((txStats.successCount / txStats.totalCount) * 100)}%`
                    : '0%'
                }
                subtitleLines={[`${(txStats?.successCount ?? 0).toLocaleString('en-NG')} successful transactions`]}
                icon={CheckCircle2}
                iconBg="bg-teal-500"
              />
            </div>
          </div>

          {stats && <AdminAnalyticsCharts stats={stats} theme={theme} />}

          <Card className={cn('flex flex-col items-start justify-between gap-4 p-5 sm:flex-row sm:items-center', shell.card)}>
            <div>
              <p className="font-semibold">View full transaction ledger</p>
              <p className={cn('text-sm', shell.cardMuted)}>
                Search, filter, and inspect every transaction with profit breakdown.
              </p>
            </div>
            <Link
              to={ROUTES.adminTransactions}
              className="inline-flex items-center gap-1 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
            >
              Open transactions
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Card>
        </>
      )}
    </div>
  );
}

export function AdminProfilePage() {
  const { admin } = useAdminAuth();
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold sm:text-2xl">Admin Profile</h1>
        <p className={cn('text-sm', shell.cardMuted)}>Your admin account details</p>
      </div>
      <Card className={cn('p-4 sm:p-5', shell.card)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className={cn('text-xs font-medium', shell.cardMuted)}>Name</p>
            <p className="font-semibold">{admin?.name}</p>
          </div>
          <div>
            <p className={cn('text-xs font-medium', shell.cardMuted)}>Email</p>
            <p className="font-semibold">{admin?.email}</p>
          </div>
          <div>
            <p className={cn('text-xs font-medium', shell.cardMuted)}>Role</p>
            <p className="font-semibold capitalize">{admin?.role.replace('_', ' ')}</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function AdminPlaceholderPage({ title }: { title: string }) {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold sm:text-2xl">{title}</h1>
        <p className={cn('text-sm', shell.cardMuted)}>This module is coming soon</p>
      </div>
      <Card className={cn('p-8 text-center text-sm', shell.card, shell.cardMuted)}>
        {title} coming soon.
      </Card>
    </div>
  );
}
